import React, {FC, useState, useEffect} from 'react';

import {DialogContent, Grid, Stack, Typography, useMediaQuery, useTheme} from '@mui/material';
import {useFormik} from 'formik';
import {t} from 'i18next';

import Button from 'components/button/Button';
import Input from 'components/input/Input';
import {ThaiPhoneNumber} from 'components/thai-phone-number/ThaiPhoneNumber';
import ReactNative from 'services/react-native';
import {CreateAddressInputType, UserDetail} from 'types';
import {removeEmojisOnly} from 'utils/address';
import {useUserDetailFormValidationSchema} from 'utils/addressFormSchema';
import {getValue} from 'utils/helper';

import {BasketDialogTitle} from './BasketDialogTitle';
import {StyledDialog, StyledBottomSheet} from './styles';

export interface IContactInformationModal {
  open: boolean;
  onClose: () => void;
  onSave: (newContactInfo: Partial<UserDetail>, formHelper: any) => void;
  values: CreateAddressInputType;
}

interface IContactInfoForm {
  userDisplayName: string;
  userMobile: string;
  userEmail: string;
}

export const ContactInformationModal: FC<IContactInformationModal> = ({
  open,
  onClose,
  onSave,
  values: initialValues
}) => {
  const [isSaveAllowed, setIsSaveAllowed] = useState(false);
  const validationSchema = useUserDetailFormValidationSchema();

  const _onSubmit = () => {
    const sanitizedName = removeEmojisOnly(values.userDisplayName.trim());
    const nameParts = sanitizedName.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '-';

    onSave({firstName, lastName, mobile: values.userMobile, email: values.userEmail}, {setSubmitting});
  };

  const {values, errors, dirty, touched, setFieldValue, handleChange, handleBlur, setSubmitting} =
    useFormik<IContactInfoForm>({
      initialValues: {
        userDisplayName: initialValues.firstName + ' ' + initialValues.lastName,
        userMobile: initialValues.mobile,
        userEmail: initialValues.email || ''
      },
      onSubmit: _onSubmit,
      validationSchema
    });

  useEffect(() => {
    const [firstName, ...lastNameParts] = values.userDisplayName.trim().split(' ');
    const lastName = lastNameParts.join(' ') || '-';

    const isNameChanged = firstName !== initialValues.firstName || lastName !== initialValues.lastName;
    const isMobileChanged = values.userMobile.replaceAll(' ', '') !== initialValues.mobile;
    const isEmailChanged = values.userEmail !== (initialValues.email || '');
    const hasChanged = isNameChanged || isMobileChanged || isEmailChanged;

    setIsSaveAllowed(hasChanged);
  }, [values, initialValues]);

  const theme = useTheme();

  const isMobileView = useMediaQuery(theme.breakpoints.only('mobile'));
  const isMobileScreen = ReactNative.isReactNativeWebView() || isMobileView;
  const ModalContainer = isMobileScreen ? StyledBottomSheet : StyledDialog;
  return (
    <ModalContainer
      className="MakroFleetDeliveryModal"
      open={open}
      onClose={onClose}
      anchor="bottom"
      ModalProps={{style: {zIndex: 1300, minWidth: '500'}}}
    >
      <BasketDialogTitle title={t('label.contactInformation')} onClose={onClose} />
      <DialogContent>
        <Stack spacing={2}>
          <Grid item mobile={12} tablet={4}>
            <Input
              id="full_name_input"
              fullWidth
              label={t('label.receiverName')}
              error={Boolean(getValue(errors, 'userDisplayName', false)) && touched.userDisplayName}
              name="userDisplayName"
              required
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.userDisplayName}
              helperText={errors?.userDisplayName}
              inputProps={{maxLength: 200}}
            />
          </Grid>

          <Grid item mobile={12} tablet={4}>
            <ThaiPhoneNumber
              id="phone_number_input"
              label={t('label.phoneNumber')}
              error={Boolean(getValue(errors, 'userMobile', false)) && touched.userMobile}
              type="text"
              fullWidth
              required
              name="userMobile"
              helperText={errors?.userMobile}
              onChange={(e) => {
                setFieldValue('userMobile', e);
              }}
              placeholder="xxx xxx xxxx"
              onBlur={handleBlur}
              value={values?.userMobile}
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              id="email_input"
              fullWidth
              label={t('label.emailOptional')}
              error={Boolean(getValue(errors, 'userEmail', false)) && touched.userEmail}
              name="userEmail"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.userEmail}
              helperText={errors.userEmail}
            />
            <Typography variant="caption" color="textSecondary" sx={{ml: 2}}>
              {t('label.emailUpdates')}
            </Typography>
          </Grid>

          <Grid xs={12} item spacing={2}>
            <Button
              fullWidth
              type="submit"
              onClick={_onSubmit}
              shape="rounded"
              variant="contained"
              disabled={
                !dirty || !!errors.userEmail || !!errors.userDisplayName || !!errors.userMobile || !isSaveAllowed
              }
              sx={{height: theme.spacing(6), px: 4}}
              id="btn_save"
              loading={false}
            >
              {t('button.save')}
            </Button>
          </Grid>
        </Stack>
      </DialogContent>
    </ModalContainer>
  );
};

ContactInformationModal.displayName = 'ContactInformationModal';
